import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { ArgyleViewBagResponse, IncomeStreamExclusionReason, IncomeStreamType } from 'src/app/models/Argyle';
import { DraftIncomeSource, StreamDecision } from 'src/app/models/income-wizard-workflow';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';
import { buildWizardSteps, WizardStep } from './wizard-step-utils';

@Component({
  selector: 'app-wizard-stream-phase',
  templateUrl: './wizard-stream-phase.component.html',
  styleUrls: ['./wizard-stream-phase.component.less'],
})
export class WizardStreamPhaseComponent implements OnInit {
  @Input() screeningId!: number;
  @Input() wizardStateId = 0;
  @Input() argyleBag: ArgyleViewBagResponse | null = null;
  @Input() drafts: DraftIncomeSource[] = [];
  @Input() streamIndex = 0;
  @Input() singleStreamMode = false;
  @Input() disabled = false;
  @Output() phaseComplete = new EventEmitter<void>();
  @Output() streamDecision = new EventEmitter<StreamDecision>();
  @Output() reviewCancelled = new EventEmitter<void>();

  readonly IncomeStreamType = IncomeStreamType;

  loading = true;
  loadError: string | null = null;
  decisionBusy = false;
  decisionError: string | null = null;

  wizardSteps: WizardStep[] = [];
  currentIndex = 0;
  applicantName = '';
  exclusionReasons: IncomeStreamExclusionReason[] = [];

  constructor(private appData: ApplicantDataService) {}

  ngOnInit(): void {
    if (this.screeningId == null || Number.isNaN(Number(this.screeningId))) {
      this.loading = false;
      this.loadError = 'Missing ScreeningID.';
      return;
    }

    this.currentIndex = Math.max(0, this.streamIndex ?? 0);
    this.loadSteps();
  }

  get currentStep(): WizardStep | null {
    if (this.currentIndex < 0 || this.currentIndex >= this.wizardSteps.length) {
      return null;
    }
    return this.wizardSteps[this.currentIndex];
  }

  get progressLabel(): string {
    const total = this.wizardSteps.length;
    if (total === 0) return 'No income streams';
    const step = this.currentStep;
    const n = this.currentIndex + 1;
    return step ? `Step ${n} of ${total} — ${step.label}` : `Step ${n} of ${total}`;
  }

  loadSteps(): void {
    this.loading = true;
    this.loadError = null;

    const bag = this.argyleBag;
    const invite = bag?.argyleInvite;
    this.applicantName = [invite?.firstName, invite?.lastName].filter(Boolean).join(' ').trim();
    this.wizardSteps = buildWizardSteps(this.drafts ?? []);

    this.appData.GetIncomeStreamExclusionReasons().subscribe({
      next: (reasons) => {
        this.exclusionReasons = reasons ?? [];
        this.loading = false;
        this.afterStepsLoaded();
      },
      error: (err: unknown) => {
        this.loading = false;
        this.loadError = this.httpErrorMessage(err, 'Could not load exclusion reasons.');
      },
    });
  }

  onStreamDecision(decision: StreamDecision): void {
    if (this.disabled || this.decisionBusy || !this.currentStep) return;

    if (this.singleStreamMode) {
      this.streamDecision.emit(decision);
      return;
    }

    this.decisionBusy = true;
    this.decisionError = null;

    this.appData
      .saveWizardDraftSource({
        ...decision.draftSource,
        wizardStateId: this.wizardStateId,
        screeningId: this.screeningId,
      })
      .subscribe({
        next: () => this.advanceAfterDecision(),
        error: (err: unknown) => {
          this.decisionBusy = false;
          this.decisionError = this.httpErrorMessage(err, 'Could not save decision.');
        },
      });
  }

  onStreamDecisionBatch(decisions: StreamDecision[]): void {
    if (this.disabled || this.decisionBusy || !this.currentStep || !decisions.length) return;

    this.decisionBusy = true;
    this.decisionError = null;

    from(decisions.map((d) => d.draftSource))
      .pipe(
        concatMap((draft) =>
          this.appData.saveWizardDraftSource({
            ...draft,
            wizardStateId: this.wizardStateId,
            screeningId: this.screeningId,
          })
        )
      )
      .subscribe({
        next: () => {},
        error: (err: unknown) => {
          this.decisionBusy = false;
          this.decisionError = this.httpErrorMessage(err, 'Could not save decision.');
        },
        complete: () => this.advanceAfterDecision(),
      });
  }

  private afterStepsLoaded(): void {
    if (this.wizardSteps.length === 0) {
      if (this.singleStreamMode) {
        this.reviewCancelled.emit();
      } else {
        this.phaseComplete.emit();
      }
      return;
    }
    if (this.currentIndex > this.wizardSteps.length) {
      this.currentIndex = this.wizardSteps.length;
    }
    if (!this.singleStreamMode) {
      this.syncToFirstPendingStep(true);
    }
  }

  private advanceAfterDecision(): void {
    const nextIndex = this.currentIndex + 1;
    this.appData.updateWizardStreamIndex(this.screeningId, nextIndex).subscribe({
      next: () => {
        this.currentIndex = nextIndex;
        this.decisionBusy = false;
        this.syncToFirstPendingStep(false);
      },
      error: (err: unknown) => {
        this.decisionBusy = false;
        this.decisionError = this.httpErrorMessage(err, 'Could not save stream progress.');
      },
    });
  }

  private syncToFirstPendingStep(persistSkipped: boolean): void {
    const start = this.currentIndex;
    while (this.currentIndex < this.wizardSteps.length && this.wizardSteps[this.currentIndex].isDecided) {
      this.currentIndex++;
    }

    if (this.currentIndex >= this.wizardSteps.length) {
      if (persistSkipped && this.currentIndex !== start) {
        this.persistIndexThenComplete();
      } else {
        this.phaseComplete.emit();
      }
      return;
    }

    if (persistSkipped && this.currentIndex !== start) {
      this.appData.updateWizardStreamIndex(this.screeningId, this.currentIndex).subscribe({
        error: () => {},
      });
    }
  }

  private persistIndexThenComplete(): void {
    this.appData.updateWizardStreamIndex(this.screeningId, this.currentIndex).subscribe({
      next: () => this.phaseComplete.emit(),
      error: () => this.phaseComplete.emit(),
    });
  }

  private httpErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'response' in body && typeof (body as { response?: unknown }).response === 'string') {
        return (body as { response: string }).response;
      }
      return err.message || fallback;
    }
    return fallback;
  }
}
